import { useQuery } from '@tanstack/react-query';

import { connectorService } from '@/services/connector-service';
import type { ConnectorInvocationStatus } from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

import { connectorQueryKeys } from './query-keys';

const ACTIVE_STATUS_POLL_DELAY_MS = 1_000;
const TERMINAL_HEARTBEAT_INTERVAL_MS = 5_000;

const FAST_POLL_STATUSES = new Set([
  'running',
  'reconciling',
  'waiting',
  'updating',
]);

const ACTIVE_INVOCATION_STATUSES = new Set([
  'queued',
  'requested',
  'running',
  'reconciling',
  'waiting',
  'updating',
]);

/**
 * Fetches the current invocation status for all features of a connector in a
 * single API call, replacing the previous per-feature parallel queries.
 *
 * Polls at 1 s when any feature is in a fast-transition state
 * (running / reconciling / waiting / updating) and at a 5 s heartbeat otherwise,
 * matching the intervals used by the previous per-feature approach.
 */
export function useListCurrentConnectorInvocationStatusesByConnector(
  tenantId: UUID | undefined,
  connectorId: UUID | undefined,
  opts?: { enabled?: boolean }
) {
  const enabled = opts?.enabled !== false && !!tenantId && !!connectorId;

  const query = useQuery<Record<UUID, ConnectorInvocationStatus>, Error>({
    queryKey: connectorQueryKeys.currentInvocationStatusesByConnector(
      tenantId,
      connectorId
    ),
    queryFn: async () => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!connectorId) throw new Error('Missing connector id');
      return connectorService.listCurrentConnectorInvocationStatusesByConnector(
        tenantId,
        connectorId
      );
    },
    enabled,
    refetchInterval: (q: {
      state: { data?: Record<UUID, ConnectorInvocationStatus> };
    }) => {
      const values = Object.values(q.state.data ?? {});
      const hasFastStatus = values.some((s) =>
        FAST_POLL_STATUSES.has(s.status)
      );
      return hasFastStatus
        ? ACTIVE_STATUS_POLL_DELAY_MS
        : TERMINAL_HEARTBEAT_INTERVAL_MS;
    },
  });

  const statuses: Record<string, ConnectorInvocationStatus> = query.data ?? {};

  // Derive run sequences directly from active statuses so consumers can stop
  // a run without an extra round-trip at click time.
  const sequences: Record<UUID, number> = {};
  for (const [featureId, status] of Object.entries(statuses)) {
    if (
      ACTIVE_INVOCATION_STATUSES.has(status.status) &&
      status.runSequence != null
    ) {
      sequences[featureId as UUID] = status.runSequence;
    }
  }

  const refetchAll = () => void query.refetch();

  // Since all features share one query, any refetch covers all of them.
  const refetchOne = (_featureId: UUID) => void query.refetch();

  return {
    statuses,
    sequences,
    refetchAll,
    refetchOne,
    isPending: query.isPending,
  };
}
